/*!
 * timers.js — the stopwatch() cue and the transport clock
 * © 2025 Rob Canning — GPLv3
 *
 * On-screen time. stopwatch() shows an overlay counting up (a fresh timer) or
 * mirroring the transport, optionally with a hold that ends it — used for
 * timed sections and countdowns. A named stopwatch also publishes its clock
 * on the control plane (elapsed / t / remaining) so the rest of the score can
 * follow it; see public/js/control/ports.js.
 *
 * The transport-time helpers (startTransportTime / pauseTransportTime …) track
 * elapsed performance time across pauses so the display and any main-clock
 * stopwatch stay honest when playback stops and starts.
 */

import { makeDraggable } from "../system/uiUtils.js";
import { scoreTimeAtWorldX } from "../system/scoreTime.js";
import { worldLeftX } from "../system/worldCoords.js";
import { truthy } from "../parser/cueUtils.js";
import { secs } from "./beats.js";
import { serverNow } from "../system/serverClock.js";
import { publish } from "../control/paramBinding.js";
import { set as busSet } from "../control/paramBus.js";
import { handleCueTrigger } from "./cueDispatcher.js";
import { onReset } from "../system/teardown.js";
import { transport } from "../system/transportState.js";

// Starts the trig:auto stopwatches among cues (the score's, by default),
// once each.
export function autostartStopwatchCues(cues = window.cues) {
  if (!cues) return;

  for (const c of cues) {
    if (!c.ast || c.ast.type !== "cueStopwatch") continue;

    // trig:auto only
    const trigPair = c.ast.args?.find(p => p.type === "trig");
    const trig = (trigPair?.value || "auto").toLowerCase();
    if (trig !== "auto") continue;

    // Prevent double autostart
    if (c._autoStarted) continue;
    c._autoStarted = true;

    handleStopwatchCue(c.ast, c.element, { fromCueTrigger: false });
  }
}

// -------------------------------------------------------
// 🚨 GLOBAL: dismiss ALL active stopwatch overlays
// -------------------------------------------------------
export function dismissAllStopwatchOverlays() {
  // remove any overlay nodes
  document.querySelectorAll('[id^="cue-stopwatch-"]').forEach(div => {
    div._stop?.();               // its clock, signal, hold and fade-out
    div.remove();
  });

  // restore original cue visibility
  if (window.cues) {
    for (const c of window.cues) {
      if (!c.ast || c.ast.type !== "cueStopwatch") continue;

      const el = c.element;
      if (!el) continue;

      try {
        // if previously hidden by stopwatch
        if (el._stopwatchHidden) {
          el.style.display = el._stopwatchHidden.display || "";
          el.style.opacity = el._stopwatchHidden.opacity || "";
          el.style.pointerEvents = el._stopwatchHidden.pointerEvents || "";
          delete el._stopwatchHidden;
        }
      } catch(e) {
        console.warn("[STOPWATCH] failed restoring cue:", e);
      }
    }
  }
}
onReset("project", dismissAllStopwatchOverlays);   // their clocks, signals and holds too


// -------------------------------------------------------
// In-score overlay stopwatch (default mode)
// Creates a positioned <div> overlay near the cue element.
// source:'new'  → fresh timer counting from 0 (default)
// source:'main' → mirrors the global transport stopwatch
// source:'gui'/'panel' → floating draggable panel (opt-in)
// -------------------------------------------------------
function _handleOverlayStopwatch(ast, cueElement, params, options) {
  const { fromCueTrigger = false } = options;

  if (!cueElement) {
    console.error("[STOPWATCH] ❌ No cueElement for overlay stopwatch");
    return;
  }

  // hold: is in beats of tempo(), as every DSL time; from here on, seconds
  const hold         = secs(Number(params.hold || 0));
  const uid          = params.uid ? String(params.uid) : null;
  // down:1 — count down: with a hold it shows the seconds left, down to 00:00. A
  // countdown needs an end, so without a hold it counts up.
  const down         = truthy(params.down);
  if (down && !(hold > 0)) console.warn("[STOPWATCH] down:1 needs hold:<beats> — counting up");
  const followScroll = truthy(params.scroll);
  const offsetX      = Number(params.offsetX || 0);
  const sourceType   = params.source || "new";
  const styleString  = params.style ? String(params.style).replace(/^['"]|['"]$/g, "") : null;

  const score = document.getElementById("scoreContainer");
  if (!score) { console.error("[STOPWATCH] ❌ No scoreContainer"); return; }

  const bbox         = cueElement.getBoundingClientRect();
  const containerBox = score.getBoundingClientRect();
  const scrollX      = score.scrollLeft || 0;
  const scrollY      = score.scrollTop  || 0;

  const x = (followScroll ? bbox.left - containerBox.left + scrollX : bbox.left) + offsetX;
  const y = (followScroll ? bbox.top  - containerBox.top  + scrollY - 10 : bbox.top - 10);

  // One display per stopwatch: by uid, else by source (the unnamed ones share)
  const divId = `cue-stopwatch-${uid || sourceType}`;
  let div = document.getElementById(divId);
  const startTime = Date.now();
  // Which clock. A stopwatch the playhead started counts SCORE time from the
  // cue's own position: world x is the one description of the score every
  // client agrees on, so every client shows the same reading regardless of
  // when each one noticed the crossing (up to the sync drift threshold).
  // It also stops with the transport, which is what "honest when playback
  // stops" means. A click- or auto-started stopwatch has no score position to
  // count from and keeps the wall clock.
  const trig = String(params.trig || "playhead").toLowerCase();
  let startMs = null;
  if (fromCueTrigger && trig !== "click" && trig !== "auto" && trig !== "manual") {
    try { startMs = scoreTimeAtWorldX(worldLeftX(cueElement), transport.scoreWidth, transport.duration); } catch { startMs = null; }
  }
  const scoreClock = Number.isFinite(startMs);
  const elapsedSec = () => scoreClock
    ? Math.max(0, ((Number(transport.elapsedTime) || 0) - startMs) / 1000)
    : (Date.now() - startTime) / 1000;
  let intervalId;

  if (div) {
    // run again: the earlier run's clock, signal, hold and fade-out all stop
    div._stop?.();
    div.textContent = "00:00";
    div.style.opacity = "1";
    div.style.transition = "none";
  } else {
    div = document.createElement("div");
    div.id = divId;
    div.className = "cue-stopwatch-display";
    div.style.position  = followScroll ? "absolute" : "fixed";
    div.style.left      = `${x}px`;
    div.style.top       = `${y}px`;
    div.style.zIndex    = "999999";

    if (styleString) {
      styleString.split(";").forEach(rule => {
        const [prop, val] = (rule || "").split(":").map(s => s && s.trim());
        if (prop && val) div.style[prop] = val;
      });
    }

    (followScroll ? score : document.body).appendChild(div);
  }

  // Position tracking via rAF — smooth, scroll-aware, no 1-second jump
  let _rafAlive = true;
  if (followScroll) {
    const trackPos = () => {
      if (!_rafAlive || !div.isConnected) return;
      const b = cueElement.getBoundingClientRect();
      div.style.left = `${b.left - containerBox.left + (score.scrollLeft || 0) + offsetX}px`;
      div.style.top  = `${b.top  - containerBox.top  + (score.scrollTop  || 0) - 10}px`;
      requestAnimationFrame(trackPos);
    };
    requestAnimationFrame(trackPos);
  }

  let _held = false;
  let signalTimer = null, holdTimer = null, removeTimer = null;
  function endHold() {
    if (_held) return;
    _held = true;
    // it has run its course: <uid>.end — what a map-mode station or a
    // trig: condition waits for (a dismissed stopwatch never says so)
    // straight to the bus: publish() is throttled, and an end landing just
    // after the last 10 Hz tick would be dropped
    if (uid) {
      busSet(`${uid}.end`, 1, { source: "stopwatch" });
      if (hold > 0) { busSet(`${uid}.t`, 1, { source: "stopwatch" }); busSet(`${uid}.remaining`, 0, { source: "stopwatch" }); }
    }
    _rafAlive = false;
    clearInterval(intervalId);
    clearInterval(signalTimer);
    div.style.transition = "opacity 1s ease";
    div.style.opacity = "0";
    removeTimer = setTimeout(() => div.remove(), 1000);
  }

  if (sourceType === "main") {
    div.textContent = getStopwatchTime();
    intervalId = setInterval(() => {
      const text = getStopwatchTime();
      if (div.textContent !== text) div.textContent = text;
    }, 100);

  } else {
    // source:'new' — fresh timer from zero. Ticks at 10 Hz: with a 1 s
    // interval each client's flip would land at its own phase, up to a second
    // apart even from the same clock.
    const render = () => {
      // counting down, the time left rounds up: 12 s shows 00:12 … 00:01, 00:00
      const elapsed = down && hold > 0
        ? Math.ceil(Math.max(0, hold - elapsedSec()))
        : Math.floor(elapsedSec());
      const m = String(Math.floor(elapsed / 60)).padStart(2, "0");
      const s = String(elapsed % 60).padStart(2, "0");
      const text = `${m}:${s}`;
      if (div.textContent !== text) div.textContent = text;
      // a hold measured on the score clock ends when the score has moved that far
      if (scoreClock && hold > 0 && elapsedSec() >= hold) endHold();
    };
    render();
    intervalId = setInterval(render, 100);

    if (!hold) {
      div.style.cursor = "pointer";
      div.onclick = () => {
        div._stop();
        div.remove();
      };
    }
  }

  div._stop = () => {
    _held = true;
    _rafAlive = false;
    clearInterval(intervalId);
    clearInterval(signalTimer);
    clearTimeout(holdTimer);
    clearTimeout(removeTimer);
  };

  // ── the clock as a signal ───────────────────────────────────────────────
  // A signal stepping once a second would visibly stair-step anything it
  // drove, so this runs at 10 Hz — and only when the cue was given a uid, so a
  // score that never names its stopwatch pays nothing for this.
  if (uid) {
    busSet(`${uid}.end`, 0, { source: "stopwatch" });
    signalTimer = setInterval(() => {
      // Self-cleaning: the overlay is removed by several different paths
      // (hold expiry, a click, a score change), and this is the one condition
      // all of them produce.
      if (!div.isConnected) { clearInterval(signalTimer); return; }
      const elapsed = elapsedSec();
      publish("stopwatch", uid, {
        elapsed,
        // A hold gives the clock an end, so it can also say how far through it
        // is. Without one there is nothing to be a fraction of.
        ...(hold > 0 ? { t: Math.min(1, elapsed / hold), remaining: Math.max(0, hold - elapsed) } : {}),
      });
    }, 100);
  }

  if (hold > 0 && !scoreClock) holdTimer = setTimeout(endHold, hold * 1000);
}

export function handleStopwatchCue(ast, cueElement = null, options = {}) {
  const { fromCueTrigger = false, fromRemote = false } = options;

  // -----------------------------------------
  // PARAM EXTRACTION
  // -----------------------------------------
  const params = {};
  for (const p of (ast.args || [])) {
    params[p.type] = p.value;
  }

  // -----------------------------------------
  // SOURCE ROUTING
  // Default: svg (in-score text that starts immediately)
  // source:gui → floating draggable panel with start/stop/reset buttons
  // -----------------------------------------
  const source = (params.source || 'svg').toLowerCase();
  if (source !== 'gui' && source !== 'panel') {
    // Default: positioned overlay div near the cue element, starts immediately.
    // source:'new'  → fresh timer from zero (default)
    // source:'main' → mirrors global transport stopwatch
    _handleOverlayStopwatch(ast, cueElement, params, options);
    return;
  }

  // -----------------------------------------
  // TRIGGER LOGIC (gui/panel mode only)
  // -----------------------------------------
  const trig = (params.trig || "auto").toLowerCase();

  // trig:playhead → only when crossing
  if (trig === "playhead" && !fromCueTrigger) {
    return;
  }

  // trig:manual → only when explicitly triggered
  if (trig === "manual" && !fromCueTrigger) {
    return;
  }

  // trig:auto → allowed in any mode (panel is position:fixed, unaffected by scroll)

  // trig:click → wire click listener on first call; start (+ broadcast) on click
  if (trig === "click" && !fromCueTrigger) {
    const trigger = () => {
      handleStopwatchCue(ast, cueElement, { fromCueTrigger: true });
      const sock = window.socket;
      if (sock?.readyState === WebSocket.OPEN) {
        sock.send(JSON.stringify({ type: "cue_triggered", cueId: cueElement.id }));
      }
    };
    // Register so socket.js can fire it on remote clients
    window._stopwatchTriggers = window._stopwatchTriggers || {};
    window._stopwatchTriggers[cueElement.id] = trigger;
    // Style element as clickable button
    cueElement.style.cursor = "pointer";
    cueElement.addEventListener("click", trigger, { once: true });
    return;
  }

  // -----------------------------------------
  // PANEL — show existing or create draggable control panel
  // -----------------------------------------
  const PANEL_ID = 'oscilla-sw-panel';
  const LS_KEY   = 'oscilla.swPanel.pos';

  let panel = document.getElementById(PANEL_ID);

  if (panel) {
    panel.style.display = '';
    // Only auto-start if triggered by a real user action, not auto-init
    if (fromCueTrigger && !fromRemote) window._swPanelControls?.start?.();
    return;
  }

  // Initial position: localStorage > cueElement bbox > fallback
  const offsetX = Number(params.offsetX || 0);
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch { /* unreadable: the default place */ }
  let initX = 60, initY = 60;
  if (saved?.x !== undefined) {
    initX = saved.x; initY = saved.y;
  } else if (cueElement) {
    const bbox = cueElement.getBoundingClientRect();
    initX = bbox.left + offsetX;
    initY = Math.max(10, bbox.top - 10);
  }

  // ── Build panel ───────────────────────────────────────────────────────────
  panel = document.createElement('div');
  panel.id = PANEL_ID;
  // trig:auto → show immediately on load; otherwise hidden until toggled
  const _showNow = (trig === 'auto' || fromCueTrigger);
  Object.assign(panel.style, {
    position: 'fixed', left: initX + 'px', top: initY + 'px',
    zIndex: '999999', background: '#f0f0f0', border: '1px solid #aaaaaa',
    borderRadius: '8px', minWidth: '200px', fontFamily: 'monospace',
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)', userSelect: 'none',
    touchAction: 'none', display: _showNow ? '' : 'none'
  });

  // Header / drag handle
  const hdr = document.createElement('div');
  Object.assign(hdr.style, {
    padding: '6px 10px', cursor: 'grab', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid #cccccc', fontSize: '11px',
    color: '#666666', letterSpacing: '1px', background: '#e4e4e4',
    borderRadius: '8px 8px 0 0'
  });
  const titleEl = document.createElement('span');
  titleEl.textContent = '\u2261  STOPWATCH';
  const xBtn = document.createElement('button');
  xBtn.textContent = '\u00d7';
  Object.assign(xBtn.style, {
    background: 'none', border: 'none', color: '#999999',
    cursor: 'pointer', fontSize: '18px', lineHeight: '1', padding: '0 2px'
  });
  xBtn.addEventListener('pointerdown', e => e.stopPropagation());
  xBtn.addEventListener('click', () => { panel.style.display = 'none'; });
  hdr.appendChild(titleEl);
  hdr.appendChild(xBtn);

  // Time display
  const swDisplay = document.createElement('div');
  Object.assign(swDisplay.style, {
    textAlign: 'center', fontSize: '2.6em', padding: '10px 16px 6px',
    color: '#111111', letterSpacing: '3px'
  });
  swDisplay.textContent = '00:00';

  // Button row
  const btnRow = document.createElement('div');
  Object.assign(btnRow.style, {
    display: 'flex', gap: '6px', padding: '8px 10px',
    borderTop: '1px solid #cccccc'
  });
  const mkBtn = (label, title) => {
    const b = document.createElement('button');
    b.textContent = label; b.title = title;
    Object.assign(b.style, {
      flex: '1', padding: '7px 4px', border: '1px solid #aaaaaa',
      borderRadius: '4px', background: '#d8d8d8', color: '#222222',
      fontFamily: 'monospace', fontSize: '15px', cursor: 'pointer'
    });
    b.addEventListener('pointerdown', e => e.stopPropagation());
    return b;
  };
  const startBtn = mkBtn('\u25b6', 'Start');
  const pauseBtn = mkBtn('\u23f8', 'Pause');
  const resetBtn = mkBtn('\u21ba', 'Reset');
  btnRow.appendChild(startBtn);
  btnRow.appendChild(pauseBtn);
  btnRow.appendChild(resetBtn);

  panel.appendChild(hdr);
  panel.appendChild(swDisplay);
  panel.appendChild(btnRow);
  document.body.appendChild(panel);

  // ── Timer state ───────────────────────────────────────────────────────────
  let _running = false, _savedBase = 0, _runStart = 0, _swInterval = null;

  function _updateSwDisplay() {
    const total = Math.floor(_savedBase / 1000);
    swDisplay.textContent =
      String(Math.floor(total / 60)).padStart(2, '0') + ':' +
      String(total % 60).padStart(2, '0');
  }

  function _swBroadcast(action) {
    if (fromRemote) return;
    const sock = window.socket;
    if (sock?.readyState === WebSocket.OPEN)
      sock.send(JSON.stringify({ type: 'stopwatch_control', action, elapsed: _savedBase }));
  }

  const controls = {
    start(nobroadcast) {
      if (_running) return;
      _running = true;
      _runStart = Date.now();
      _swInterval = setInterval(() => {
        const cur = (Date.now() - _runStart) + _savedBase;
        const total = Math.floor(cur / 1000);
        swDisplay.textContent =
          String(Math.floor(total / 60)).padStart(2, '0') + ':' +
          String(total % 60).padStart(2, '0');
      }, 250);
      startBtn.style.background = '#90cc90';
      if (!nobroadcast) _swBroadcast('start');
    },
    pause(nobroadcast) {
      if (!_running) return;
      _running = false;
      _savedBase += Date.now() - _runStart;
      clearInterval(_swInterval);
      startBtn.style.background = '#d8d8d8';
      if (!nobroadcast) _swBroadcast('pause');
    },
    reset(nobroadcast) {
      controls.pause(true);
      _savedBase = 0;
      _updateSwDisplay();
      if (!nobroadcast) _swBroadcast('reset');
    },
    syncElapsed(ms) { _savedBase = ms; _updateSwDisplay(); }
  };

  window._swPanelControls = controls;

  startBtn.addEventListener('click', () => controls.start());
  pauseBtn.addEventListener('click', () => controls.pause());
  resetBtn.addEventListener('click', () => controls.reset());

  // ── Drag + save position ──────────────────────────────────────────────────
  makeDraggable(panel, hdr);
  hdr.addEventListener('pointerup', () => {
    const r = panel.getBoundingClientRect();
    localStorage.setItem(LS_KEY, JSON.stringify({ x: r.left, y: r.top }));
  });

  // Auto-start only when triggered by user action (not trig:auto init)
  if (fromCueTrigger && !fromRemote) controls.start(true);

  // Broadcast panel creation to other clients
  if (fromCueTrigger && !fromRemote && cueElement?.id) {
    const sock = window.socket;
    if (sock?.readyState === WebSocket.OPEN)
      sock.send(JSON.stringify({ type: 'stopwatch_start', elementId: cueElement.id }));
  }
}

// Direct API — call from score extensions to show the stopwatch panel.
// Also applies any pending sync that arrived before the panel was created.
window._showStopwatchPanel = function() {
  handleStopwatchCue(
    { type: 'cueStopwatch', args: [{ type: 'source', value: 'gui' }, { type: 'trig', value: 'auto' }] },
    null,
    { fromCueTrigger: false, fromRemote: false }
  );
  // Apply any pending sync from socket (late-join or control message).
  // Add time elapsed since the message was received so the display is current.
  const pending = window._pendingSwPanelSync;
  if (pending && window._swPanelControls) {
    const ctrl = window._swPanelControls;
    const drift = pending.action === 'start' && pending.receivedAt
      ? Date.now() - pending.receivedAt
      : 0;
    const elapsed = (pending.elapsed || 0) + drift;
    ctrl.pause(true);
    ctrl.syncElapsed(elapsed);
    if (pending.action === 'start') ctrl.start(true);
    window._pendingSwPanelSync = null;
  }
};

export function getStopwatchTime() {
  const display = document.getElementById("stopwatch");
  return display ? display.textContent : "00:00";
}

/** Raw elapsed seconds from the performance stopwatch — used by fullscreen
 *  overlay to compute the display string in sync with the wall clock tick. */
export function getStopwatchSeconds() {
  // The room's clock when the server has given us one and we know its time
  // (serverClock.js): the same reading on every client, whenever it joined.
  const pc = window._perfClock;
  const sn = pc ? serverNow() : null;
  if (pc && sn != null) {
    const running = pc.startedAt != null ? Math.max(0, sn - pc.startedAt) : 0;
    return (Number(pc.accumulated) || 0) / 1000 + running / 1000;
  }
  // Before the first sync there is no room clock yet
  return 0;
}

// The top-bar display follows getStopwatchSeconds at 5 Hz whatever the source
// -- the server's clock keeps moving whether or not this client thinks it is
// running, so the display cannot be driven by the local start/stop.
let _displayTimer = null;
function ensureDisplayLoop() {
  if (_displayTimer) return;
  _displayTimer = setInterval(() => updateDisplay(getStopwatchSeconds()), 200);
}
if (typeof document !== "undefined") {
  if (document.body) ensureDisplayLoop(); else document.addEventListener("DOMContentLoaded", ensureDisplayLoop, { once: true });
}


// ============================================================================
// Performance stopwatch — the top-bar display of the transport's clock
// (server.js perf_clock_*), shown the same on every client of the project.
// ============================================================================

/**
 * Resets all stopwatch timing.
 */
export function resetStopwatch() {
  updateDisplay(0);
  // the transport's clock resets for everyone on it (server.js perf_clock_reset)
  const sock = window.socket;
  if (sock?.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: "perf_clock_reset" }));
}

/**
 * Updates the DOM element with formatted time (MM:SS).
 */
function updateDisplay(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const display = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  const el = document.getElementById('stopwatch');
  if (el) el.textContent = display;
}


/**
 * Sets up fullscreen toggle behavior for the stopwatch when clicked.
 * Creates an overlay with main timer, mini timer (click to swap), and close button.
 * Supports multiple view modes: solid, blur, transparent
 * Includes countdown sequences feature
 */

// Countdown sequences storage
let countdownSequences = [];
let isCountdownMode = false;
// Latest countdown state from server — cached for interpolation in updateTimes()
let _latestCountdownState = null;
let _latestCountdownReceivedAt = 0;

// Network sync disabled flag
window.auxwatchNetworkDisabled = localStorage.getItem("oscilla.auxwatchNetworkDisabled") === "true";

// Load sequences from localStorage
function loadCountdownSequences() {
  try {
    const saved = localStorage.getItem("oscilla.countdownSequences");
    if (saved) {
      countdownSequences = JSON.parse(saved);
      // Sync to server after loading (with delay to ensure socket ready)
      syncSequencesToServer();
    }
  } catch (e) {
    console.warn("[Countdown] Failed to load sequences:", e);
  }
}

// Sync sequences to server (called on load and socket connect)
export function syncSequencesToServer() {
  if (window.auxwatchNetworkDisabled) return;
  if (countdownSequences.length === 0) return;
  
  // Delay to ensure socket is ready
  const attemptSync = (retries = 5) => {
    if (window.socket && window.socket.readyState === WebSocket.OPEN) {
      broadcastSequencesUpdate();
    } else if (retries > 0) {
      setTimeout(() => attemptSync(retries - 1), 200);
    } else {
      console.warn("[Countdown] ⚠️ Could not sync sequences - socket not ready");
    }
  };
  
  attemptSync();
}

// Expose for external use (e.g., on socket connect)
window.syncCountdownSequences = syncSequencesToServer;

// Save sequences to localStorage
function saveCountdownSequences() {
  try {
    localStorage.setItem("oscilla.countdownSequences", JSON.stringify(countdownSequences));
    // Also refresh the fullscreen dropdown
    if (typeof window._refreshCountdownDropdown === 'function') {
      window._refreshCountdownDropdown();
    }
  } catch (e) {
    console.warn("[Countdown] Failed to save sequences:", e);
  }
}

// Export sequences as JSON file
function exportCountdownSequences() {
  const blob = new Blob([JSON.stringify(countdownSequences, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "countdown-sequences.json";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);   // after the download has read it
}

// Import sequences from JSON file
function importCountdownSequences(file) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const imported = JSON.parse(e.target.result);
      if (Array.isArray(imported)) {
        countdownSequences = imported;
        saveCountdownSequences();
        // Sync imported sequences to server
        syncSequencesToServer();
      }
    } catch (err) {
      console.error("[Countdown] Import failed:", err);
    }
  };
  reader.readAsText(file);
}

// =====================================================
// SERVER-OWNED COUNTDOWN - CLIENT JUST DISPLAYS
// =====================================================

// Send commands to server (server owns the timer)
function sendCountdownStartCue(cue) {
  if (window.auxwatchNetworkDisabled) {
    return;
  }
  if (!window.socket || window.socket.readyState !== WebSocket.OPEN) {
    return;
  }
  
  // Ensure server has latest sequences before starting
  broadcastSequencesUpdate();
  
  window.socket.send(JSON.stringify({
    type: "countdown_start_cue",
    cue: cue
  }));
}

function sendCountdownStartSequence(sequenceIndex) {
  if (window.auxwatchNetworkDisabled) {
    return;
  }
  if (!window.socket || window.socket.readyState !== WebSocket.OPEN) {
    return;
  }
  
  // Get the actual sequence data to send with the command
  const sequence = countdownSequences[sequenceIndex];
  if (!sequence) {
    console.warn("[Countdown] ⚠️ Sequence not found at index:", sequenceIndex);
    return;
  }
  
  // First ensure server has latest sequences
  broadcastSequencesUpdate();
  
  // Then send start command with sequence data included
  window.socket.send(JSON.stringify({
    type: "countdown_start_sequence",
    sequenceIndex: sequenceIndex,
    sequence: sequence  // Include full sequence data as backup
  }));
}

function sendCountdownStop() {
  if (window.auxwatchNetworkDisabled) return;
  if (!window.socket || window.socket.readyState !== WebSocket.OPEN) return;
  
  window.socket.send(JSON.stringify({
    type: "countdown_stop"
  }));
}

// Broadcast sequences to server (for storage and sharing)
function broadcastSequencesUpdate() {
  if (window.auxwatchNetworkDisabled) {
    return;
  }
  if (!window.socket || window.socket.readyState !== WebSocket.OPEN) {
    return;
  }
  
  window.socket.send(JSON.stringify({
    type: "countdown_sequences_update",
    sequences: countdownSequences
  }));
}

// Another client edited the countdown sequences (socket.js routes the message here)
export function handleCountdownMessage(data) {
  if (data.type === "countdown_sequences_update") {
    if (window.auxwatchNetworkDisabled) return;
    if (data.sequences && Array.isArray(data.sequences)) {
      countdownSequences = data.sequences;
      saveCountdownSequences();
      // Re-render if editor is open
      const list = document.getElementById("countdown-sequences-list");
      if (list) {
        // Will be rendered by the function inside the closure
        window._renderCountdownSequences?.();
      }
    }
  }
}

// Update display from server sync state (called from oscillaSystemSocket)
// Track previous state to detect completion
let _previousCountdownRunning = false;

function updateCountdownDisplay(countdown) {
  const stopwatchEl = document.getElementById("stopwatch");
  const mainLabelEl = document.getElementById("fullscreen-main-label");
  const miniLabelEl = document.getElementById("fullscreen-mini-label");
  
  const isRunning = countdown && countdown.running;
  
  // Determine which display is the "Timer" (not Performance)
  // showingPerformanceTime means: main=Performance, mini=Timer
  // !showingPerformanceTime means: main=Timer, mini=Performance
  const showingPerfInMain = window._showingPerformanceTime || false;
  
  // Timer elements (where countdown should display)
  const timerLabelEl = showingPerfInMain ? miniLabelEl : mainLabelEl;
  
  if (isRunning) {
    isCountdownMode = true;

    // Cache server state + timestamp for interpolation in updateTimes()
    // updateTimes() owns the fullscreen timer display so it stays phase-locked
    // with the clock. We only update the topbar and label here.
    _latestCountdownState = countdown;
    _latestCountdownReceivedAt = Date.now();

    const min = Math.floor(countdown.remainingSec / 60);
    const sec = countdown.remainingSec % 60;
    const timeStr = `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;

    // Update topbar stopwatch (always shows countdown when running)
    if (stopwatchEl) {
      stopwatchEl.textContent = timeStr;
      stopwatchEl.title = countdown.cueName || "Countdown";
    }

    // Update label only — time display handled by updateTimes()
    if (timerLabelEl) timerLabelEl.textContent = countdown.cueName || "Countdown";
    
  } else if (isCountdownMode) {
    // Countdown stopped
    isCountdownMode = false;
    
    // Reset topbar
    if (stopwatchEl) {
      stopwatchEl.title = "Stopwatch";
    }
    
    // Reset Timer label in fullscreen
    if (timerLabelEl) {
      timerLabelEl.textContent = "Timer";
    }
  }
  
  // Update play/stop button state
  if (typeof window._updateCountdownControlsPlayButton === 'function') {
    window._updateCountdownControlsPlayButton(isRunning);
  }
  
  // Detect completion (was running, now stopped)
  if (_previousCountdownRunning && !isRunning) {
    if (typeof window._handleCountdownComplete === 'function') {
      window._handleCountdownComplete();
    }
  }
  
  _previousCountdownRunning = isRunning;
}

// Expose for oscillaSystemSocket to call
window.updateCountdownDisplay = updateCountdownDisplay;


export function setupStopwatchFullscreenToggle() {
    window.addEventListener('DOMContentLoaded', () => {
      const transportTime = document.getElementById("transport-time");
      const mainContent = document.getElementById("scoreContainer");
  
      if (!transportTime || !mainContent) {
        console.error("[ERROR] transport-time or scoreContainer not found.");
        return;
      }
      
      // Load saved sequences
      loadCountdownSequences();
      
      let fullscreenOverlay = null;
      let showingPerformanceTime = false; // false = rehearsal (main), true = performance (main)
      let fullscreenUpdateInterval = null;
      let fullscreenStartTimer = null;    // waits for the next whole second, then starts the interval
      
      // View modes: 'solid', 'blur', 'transparent'
      const viewModes = ['solid', 'blur', 'transparent'];
      let currentViewMode = 0; // Start with solid
      
      function createFullscreenOverlay() {
        const overlay = document.createElement("div");
        overlay.id = "stopwatch-fullscreen-overlay";
        overlay.className = "stopwatch-fullscreen-overlay mode-solid";
        
        // Close button (top right)
        const closeBtn = document.createElement("button");
        closeBtn.className = "fullscreen-close-btn";
        closeBtn.innerHTML = "✕";
        closeBtn.title = "Close fullscreen (Escape)";
        closeBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          exitFullscreen();
        });
        
        // View mode button (top left)
        const modeBtn = document.createElement("button");
        modeBtn.className = "fullscreen-mode-btn";
        modeBtn.id = "fullscreen-mode-btn";
        modeBtn.innerHTML = "◐";
        modeBtn.title = "Change view mode";
        modeBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          cycleViewMode();
        });
        
        // Mode label
        const modeLabel = document.createElement("span");
        modeLabel.className = "fullscreen-mode-label";
        modeLabel.id = "fullscreen-mode-label";
        modeLabel.textContent = "solid";
        
        // Main time label
        const mainLabel = document.createElement("div");
        mainLabel.className = "fullscreen-main-label";
        mainLabel.id = "fullscreen-main-label";
        mainLabel.textContent = "Timer";
        
        // Main time display
        const mainTime = document.createElement("div");
        mainTime.className = "fullscreen-main-time";
        mainTime.id = "fullscreen-main-time";
        mainTime.textContent = "00:00";
        
        // Main time container (wraps label + time)
        const mainContainer = document.createElement("div");
        mainContainer.className = "fullscreen-main-container";
        mainContainer.id = "fullscreen-main-container";
        mainContainer.appendChild(mainLabel);
        mainContainer.appendChild(mainTime);
        
        // Countdown editor button (red ring) - always attached to countdown controls
        const countdownBtn = document.createElement("button");
        countdownBtn.className = "fullscreen-countdown-btn";
        countdownBtn.id = "fullscreen-countdown-btn";
        countdownBtn.title = "Edit Sequences";
        countdownBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          toggleCountdownEditor();
        });
        
        // Mini container (clickable to swap) - for performance time initially
        const miniContainer = document.createElement("div");
        miniContainer.className = "fullscreen-mini-container";
        miniContainer.id = "fullscreen-mini-container";
        miniContainer.title = "Click to swap timers";
        miniContainer.addEventListener("click", (e) => {
          e.stopPropagation();
          swapTimers();
        });
        
        // Mini time label
        const miniLabel = document.createElement("div");
        miniLabel.className = "fullscreen-mini-label";
        miniLabel.id = "fullscreen-mini-label";
        miniLabel.textContent = "Performance";
        
        // Mini time display
        const miniTime = document.createElement("div");
        miniTime.className = "fullscreen-mini-time";
        miniTime.id = "fullscreen-mini-time";
        miniTime.textContent = "00:00";
        
        miniContainer.appendChild(miniLabel);
        miniContainer.appendChild(miniTime);
        
        // Clock container (actual time of day)
        const clockContainer = document.createElement("div");
        clockContainer.className = "fullscreen-clock-container";
        
        // Clock label
        const clockLabel = document.createElement("div");
        clockLabel.className = "fullscreen-mini-label";
        clockLabel.textContent = "Clock";
        
        // Clock time display
        const clockTime = document.createElement("div");
        clockTime.className = "fullscreen-mini-time";
        clockTime.id = "fullscreen-clock-time";
        clockTime.textContent = "00:00";
        
        clockContainer.appendChild(clockLabel);
        clockContainer.appendChild(clockTime);
        
        // =====================================================
        // COUNTDOWN CONTROLS CONTAINER (Play/Stop + Dropdown)
        // =====================================================
        const countdownControlsContainer = document.createElement("div");
        countdownControlsContainer.className = "fullscreen-countdown-controls-container";
        countdownControlsContainer.id = "fullscreen-countdown-controls";
        
        // Controls row (button + dropdown)
        const controlsRow = document.createElement("div");
        controlsRow.className = "fullscreen-countdown-controls-row";
        
        // Play/Stop button
        const playStopBtn = document.createElement("button");
        playStopBtn.id = "fullscreen-countdown-play-btn";
        playStopBtn.className = "fullscreen-countdown-play-btn";
        playStopBtn.innerHTML = "▶";
        playStopBtn.title = "Start";
        playStopBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          handlePlayStopClick();
        });
        
        // Dropdown button - just the selection text
        const dropdownBtn = document.createElement("button");
        dropdownBtn.id = "fullscreen-countdown-dropdown-btn";
        dropdownBtn.className = "fullscreen-countdown-dropdown-btn";
        dropdownBtn.innerHTML = '<span id="fullscreen-countdown-selection">—</span>';
        dropdownBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          toggleCountdownDropdown();
        });
        
        // Dropdown menu (hidden by default)
        const dropdownMenu = document.createElement("div");
        dropdownMenu.id = "fullscreen-countdown-dropdown-menu";
        dropdownMenu.className = "fullscreen-countdown-dropdown-menu hidden";
        
        controlsRow.appendChild(playStopBtn);
        controlsRow.appendChild(dropdownBtn);
        
        // Red ring button (editor toggle) - always on countdown controls
        countdownControlsContainer.appendChild(countdownBtn);
        countdownControlsContainer.appendChild(controlsRow);
        countdownControlsContainer.appendChild(dropdownMenu);
        
        // Bottom row for mini containers - three equal columns
        const bottomRow = document.createElement("div");
        bottomRow.className = "fullscreen-bottom-row";
        bottomRow.appendChild(miniContainer);
        bottomRow.appendChild(clockContainer);
        bottomRow.appendChild(countdownControlsContainer);
        
        // Top controls container
        const topControls = document.createElement("div");
        topControls.className = "fullscreen-top-controls";
        topControls.appendChild(modeBtn);
        topControls.appendChild(modeLabel);
        
        // Assemble
        overlay.appendChild(topControls);
        overlay.appendChild(closeBtn);
        overlay.appendChild(mainContainer);
        overlay.appendChild(bottomRow);
        
        // Prevent clicks on overlay background from doing anything
        overlay.addEventListener("click", (e) => {
          e.stopPropagation();
        });
        
        return overlay;
      }
      
      // =====================================================
      // COUNTDOWN EDITOR
      // =====================================================
      
      function toggleCountdownEditor() {
        const existing = document.getElementById("countdown-editor");
        if (existing) {
          existing.remove();
          return;
        }
        createCountdownEditor();
      }
      
      function createCountdownEditor() {
        const editor = document.createElement("div");
        editor.id = "countdown-editor";
        editor.className = "osc-panel osc-panel--wide visible";
        editor.style.position = "absolute";
        editor.style.top = "70px";
        editor.style.right = "20px";
        editor.style.maxHeight = "70vh";
        
        // Header
        const header = document.createElement("div");
        header.className = "osc-dialog-header";
        header.innerHTML = `<span class="osc-dialog-title">Countdown Sequences</span>`;
        
        const closeEditorBtn = document.createElement("button");
        closeEditorBtn.className = "osc-dialog-close";
        closeEditorBtn.innerHTML = "✕";
        closeEditorBtn.onclick = () => {
          editor.remove();
        };
        header.appendChild(closeEditorBtn);
        
        // Network sync checkbox row
        const syncRow = document.createElement("div");
        syncRow.className = "osc-dialog-checkbox-row";
        
        const syncChk = document.createElement("input");
        syncChk.type = "checkbox";
        syncChk.id = "countdown-network-sync";
        syncChk.checked = !window.auxwatchNetworkDisabled;
        syncChk.onchange = () => {
          window.auxwatchNetworkDisabled = !syncChk.checked;
          localStorage.setItem("oscilla.auxwatchNetworkDisabled", window.auxwatchNetworkDisabled);
        };
        
        const syncLabel = document.createElement("label");
        syncLabel.htmlFor = "countdown-network-sync";
        syncLabel.textContent = "Network Sync";
        
        syncRow.appendChild(syncChk);
        syncRow.appendChild(syncLabel);
        
        // Sequences list
        const listContainer = document.createElement("div");
        listContainer.className = "osc-dialog-body";
        listContainer.id = "countdown-sequences-list";
        
        // Add sequence button
        const addSeqBtn = document.createElement("button");
        addSeqBtn.className = "osc-dialog-btn osc-dialog-btn--danger";
        addSeqBtn.style.width = "calc(100% - 20px)";
        addSeqBtn.style.margin = "10px";
        addSeqBtn.textContent = "+ Add Sequence";
        addSeqBtn.onclick = () => addNewSequence();
        
        // Import/Export buttons
        const ioRow = document.createElement("div");
        ioRow.className = "osc-dialog-footer";
        
        const exportBtn = document.createElement("button");
        exportBtn.className = "osc-dialog-btn osc-dialog-btn--ghost";
        exportBtn.textContent = "Export";
        exportBtn.onclick = exportCountdownSequences;
        
        const importBtn = document.createElement("button");
        importBtn.className = "osc-dialog-btn osc-dialog-btn--ghost";
        importBtn.textContent = "Import";
        importBtn.onclick = () => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = ".json";
          input.onchange = (e) => {
            if (e.target.files[0]) {
              importCountdownSequences(e.target.files[0]);
              setTimeout(() => renderSequencesList(), 100);
            }
          };
          input.click();
        };
        
        ioRow.appendChild(exportBtn);
        ioRow.appendChild(importBtn);
        
        editor.appendChild(header);
        editor.appendChild(syncRow);
        editor.appendChild(listContainer);
        editor.appendChild(addSeqBtn);
        editor.appendChild(ioRow);
        
        document.getElementById("stopwatch-fullscreen-overlay").appendChild(editor);
        
        // Make editor draggable by its header
        makeDraggable(editor, header);
        
        renderSequencesList();
      }
      
      function renderSequencesList() {
        const list = document.getElementById("countdown-sequences-list");
        if (!list) return;
        
        list.innerHTML = "";
        
        countdownSequences.forEach((seq, seqIdx) => {
          const seqEl = document.createElement("div");
          seqEl.className = "osc-dialog-section--grouped";
          seqEl.style.marginBottom = "10px";
          
          // Sequence header
          const seqHeader = document.createElement("div");
          seqHeader.className = "osc-dialog-row";
          
          const nameInput = document.createElement("input");
          nameInput.type = "text";
          nameInput.value = seq.name || "Untitled";
          nameInput.className = "osc-dialog-input";
          nameInput.style.flex = "1";
          nameInput.style.fontWeight = "600";
          nameInput.onchange = () => {
            seq.name = nameInput.value;
            saveCountdownSequences();
          };
          
          const playSeqBtn = document.createElement("button");
          playSeqBtn.className = "osc-dialog-btn";
          playSeqBtn.textContent = "▶";
          playSeqBtn.title = "Play sequence";
          playSeqBtn.onclick = () => startSequence(seqIdx);
          
          const deleteSeqBtn = document.createElement("button");
          deleteSeqBtn.className = "osc-dialog-btn osc-dialog-btn--danger";
          deleteSeqBtn.textContent = "🗑";
          deleteSeqBtn.title = "Delete sequence";
          deleteSeqBtn.onclick = () => {
            countdownSequences.splice(seqIdx, 1);
            saveCountdownSequences();
            renderSequencesList();
          };
          
          seqHeader.appendChild(nameInput);
          seqHeader.appendChild(playSeqBtn);
          seqHeader.appendChild(deleteSeqBtn);
          
          // Loop and chain controls row
          const loopRow = document.createElement("div");
          loopRow.className = "osc-dialog-row";
          loopRow.style.fontSize = "var(--osc-font-size-sm, 11px)";
          
          // Loop control
          const loopLabel = document.createElement("span");
          loopLabel.textContent = "Loop:";
          loopLabel.style.minWidth = "auto";
          
          const loopInput = document.createElement("input");
          loopInput.type = "number";
          loopInput.value = seq.loop ?? 1;
          loopInput.min = 0;
          loopInput.className = "osc-dialog-input";
          loopInput.style.width = "45px";
          loopInput.style.textAlign = "center";
          loopInput.title = "0 = infinite, 1 = play once, 2+ = repeat";
          loopInput.onchange = () => {
            seq.loop = parseInt(loopInput.value) || 0;
            loopHint.textContent = loopMark();
            saveCountdownSequences();
            broadcastSequencesUpdate();
          };
          
          const loopHint = document.createElement("span");
          loopHint.className = "osc-dialog-hint";
          // 0 loops for ever (the server turns it into Infinity); anything else repeats
          const loopMark = () => (seq.loop === 0 ? "∞" : "×");
          loopHint.textContent = loopMark();
          
          // Chain to next sequence
          const chainLabel = document.createElement("span");
          chainLabel.textContent = "Then:";
          chainLabel.style.marginLeft = "10px";
          
          const chainSelect = document.createElement("select");
          chainSelect.className = "osc-dialog-input";
          chainSelect.style.maxWidth = "120px";
          chainSelect.title = "Chain to another sequence after this one";
          
          // Add "none" option
          const noneOpt = document.createElement("option");
          noneOpt.value = "";
          noneOpt.textContent = "— stop —";
          chainSelect.appendChild(noneOpt);
          
          // Add all other sequences as options
          countdownSequences.forEach((otherSeq, otherIdx) => {
            if (otherIdx !== seqIdx) {
              const opt = document.createElement("option");
              opt.value = otherIdx;
              opt.textContent = otherSeq.name || `Sequence ${otherIdx + 1}`;
              if (seq.chain === otherIdx) opt.selected = true;
              chainSelect.appendChild(opt);
            }
          });
          
          chainSelect.onchange = () => {
            const val = chainSelect.value;
            seq.chain = val === "" ? null : parseInt(val);
            saveCountdownSequences();
            broadcastSequencesUpdate();
          };
          
          loopRow.appendChild(loopLabel);
          loopRow.appendChild(loopInput);
          loopRow.appendChild(chainLabel);
          loopRow.appendChild(chainSelect);
          
          // Cues list
          const cuesContainer = document.createElement("div");
          cuesContainer.className = "osc-dialog-section";
          cuesContainer.style.padding = "8px";
          
          (seq.cues || []).forEach((cue, cueIdx) => {
            const cueEl = document.createElement("div");
            cueEl.className = "osc-dialog-row";
            
            const cueNameInput = document.createElement("input");
            cueNameInput.type = "text";
            cueNameInput.value = cue.name || "";
            cueNameInput.placeholder = "Name";
            cueNameInput.className = "osc-dialog-input";
            cueNameInput.style.flex = "1";
            cueNameInput.style.minWidth = "80px";
            cueNameInput.onchange = () => {
              cue.name = cueNameInput.value;
              saveCountdownSequences();
            };
            
            const cueDurInput = document.createElement("input");
            cueDurInput.type = "number";
            cueDurInput.value = cue.seconds || 0;
            cueDurInput.placeholder = "Sec";
            cueDurInput.className = "osc-dialog-input";
            cueDurInput.style.width = "50px";
            cueDurInput.style.textAlign = "right";
            cueDurInput.min = 1;
            cueDurInput.onchange = () => {
              cue.seconds = parseInt(cueDurInput.value) || 0;
              saveCountdownSequences();
            };
            
            // onComplete cue trigger field
            const onCompleteInput = document.createElement("input");
            onCompleteInput.type = "text";
            onCompleteInput.value = cue.onComplete || "";
            onCompleteInput.placeholder = "onComplete, e.g. nav(scroll@B)";
            onCompleteInput.className = "osc-dialog-input";
            onCompleteInput.style.flex = "1";
            onCompleteInput.style.minWidth = "60px";
            onCompleteInput.style.fontFamily = "var(--osc-font-mono, monospace)";
            onCompleteInput.style.fontSize = "var(--osc-font-size-sm, 11px)";
            onCompleteInput.title = "Cue to trigger when this countdown slot finishes";
            onCompleteInput.onchange = () => {
              cue.onComplete = onCompleteInput.value.trim() || undefined;
              saveCountdownSequences();
              broadcastSequencesUpdate();
            };
            
            const playCueBtn = document.createElement("button");
            playCueBtn.className = "osc-dialog-btn";
            playCueBtn.textContent = "▶";
            playCueBtn.title = "Play this cue only";
            playCueBtn.onclick = () => startSingleCountdown(cue);
            
            const deleteCueBtn = document.createElement("button");
            deleteCueBtn.className = "osc-dialog-btn osc-dialog-btn--danger";
            deleteCueBtn.textContent = "✕";
            deleteCueBtn.onclick = () => {
              seq.cues.splice(cueIdx, 1);
              saveCountdownSequences();
              renderSequencesList();
            };
            
            cueEl.appendChild(cueNameInput);
            cueEl.appendChild(cueDurInput);
            cueEl.appendChild(document.createTextNode("s"));
            cueEl.appendChild(onCompleteInput);
            cueEl.appendChild(playCueBtn);
            cueEl.appendChild(deleteCueBtn);
            
            cuesContainer.appendChild(cueEl);
          });
          
          // Add cue button
          const addCueBtn = document.createElement("button");
          addCueBtn.className = "osc-dialog-btn osc-dialog-btn--ghost";
          addCueBtn.style.width = "100%";
          addCueBtn.textContent = "+ Add Cue";
          addCueBtn.onclick = () => {
            seq.cues = seq.cues || [];
            seq.cues.push({ name: "", seconds: 60 });
            saveCountdownSequences();
            renderSequencesList();
          };
          
          seqEl.appendChild(seqHeader);
          seqEl.appendChild(loopRow);
          seqEl.appendChild(cuesContainer);
          seqEl.appendChild(addCueBtn);
          
          list.appendChild(seqEl);
        });
      }
      
      function addNewSequence() {
        countdownSequences.push({
          name: "New Sequence",
          loop: 1,
          chain: null,
          cues: [{ name: "Section 1", seconds: 60 }]
        });
        saveCountdownSequences();
        broadcastSequencesUpdate();
        renderSequencesList();
      }
      
      // =====================================================
      // COUNTDOWN PLAYBACK - SEND COMMANDS TO SERVER
      // Server owns the timer, clients just display
      // =====================================================
      
      function startSequence(seqIdx) {
        // Send command to server - server will manage the countdown
        sendCountdownStartSequence(seqIdx);
      }
      
      function startSingleCountdown(cue) {
        // Send command to server
        sendCountdownStartCue(cue);
      }
      
      function stopCountdown() {
        sendCountdownStop();
        isCountdownMode = false;
        
        // Reset display
        const mainLabel = document.getElementById("fullscreen-main-label");
        if (mainLabel) {
          mainLabel.textContent = showingPerformanceTime ? "Performance" : "Timer";
        }
        
        updatePlayStopButton(false);
      }
      
      // =====================================================
      // DISCRETE COUNTDOWN CONTROLS (in fullscreen overlay)
      // =====================================================
      
      // State for countdown controls
      let currentSelectionType = localStorage.getItem('oscilla.countdownControls.type') || 'sequence';
      let currentSelectionIndex = parseInt(localStorage.getItem('oscilla.countdownControls.index')) || 0;
      let countdownIsRunning = false;
      let selectionIsCued = false;  // True when user selected something different from what's playing
      
      function saveControlsSelection() {
        localStorage.setItem('oscilla.countdownControls.type', currentSelectionType);
        localStorage.setItem('oscilla.countdownControls.index', currentSelectionIndex.toString());
      }
      
      function handlePlayStopClick() {
        const btn = document.getElementById('fullscreen-countdown-play-btn');
        const isShowingStop = btn && btn.innerHTML === '⏹';
        
        if (isShowingStop) {
          // Button shows stop - stop the countdown
          stopCountdown();
          selectionIsCued = false;
        } else {
          // Button shows play - start the current selection
          startCurrentSelection();
          selectionIsCued = false;  // Now playing what we selected
        }
      }
      
      function startCurrentSelection() {
        if (currentSelectionType === 'sequence') {
          if (countdownSequences[currentSelectionIndex]) {
            startSequence(currentSelectionIndex);
          }
        } else {
          const allCues = getAllCuesFlat();
          const cue = allCues[currentSelectionIndex];
          if (cue) {
            startSingleCountdown(cue);
          }
        }
      }
      
      function getAllCuesFlat() {
        const cues = [];
        countdownSequences.forEach((seq, seqIdx) => {
          (seq.cues || []).forEach((cue, cueIdx) => {
            cues.push({
              ...cue,
              _seqIndex: seqIdx,
              _cueIndex: cueIdx,
              _seqName: seq.name
            });
          });
        });
        return cues;
      }
      
      function updatePlayStopButton(isPlaying) {
        countdownIsRunning = isPlaying;
        const btn = document.getElementById('fullscreen-countdown-play-btn');
        if (!btn) return;
        
        // If user has cued up a different selection, keep showing play button
        if (selectionIsCued) {
          btn.innerHTML = '▶';
          btn.title = 'Start';
          return;
        }
        
        btn.innerHTML = isPlaying ? '⏹' : '▶';
        btn.title = isPlaying ? 'Stop' : 'Start';
      }
      
      function toggleCountdownDropdown() {
        const menu = document.getElementById('fullscreen-countdown-dropdown-menu');
        if (!menu) return;
        
        if (menu.classList.contains('hidden')) {
          renderCountdownDropdown();
          menu.classList.remove('hidden');
        } else {
          menu.classList.add('hidden');
        }
      }
      
      function renderCountdownDropdown() {
        const menu = document.getElementById('fullscreen-countdown-dropdown-menu');
        if (!menu) return;
        
        menu.innerHTML = '';
        
        // Sequences section
        if (countdownSequences.length > 0) {
          const seqHeader = document.createElement('div');
          seqHeader.className = 'countdown-dropdown-header';
          seqHeader.textContent = 'Sequences';
          menu.appendChild(seqHeader);
          
          countdownSequences.forEach((seq, idx) => {
            const item = document.createElement('div');
            item.className = 'countdown-dropdown-item';
            if (currentSelectionType === 'sequence' && currentSelectionIndex === idx) {
              item.classList.add('selected');
            }
            
            const cueCount = seq.cues?.length || 0;
            const totalTime = (seq.cues || []).reduce((sum, c) => sum + (c.seconds || 0), 0);
            const timeStr = formatSecondsCompact(totalTime);
            
            item.innerHTML = `
              <span class="item-name">${seq.name || 'Unnamed'}</span>
              <span class="item-meta">${cueCount} cues · ${timeStr}</span>
            `;
            item.addEventListener('click', (e) => {
              e.stopPropagation();
              selectDropdownItem('sequence', idx);
            });
            menu.appendChild(item);
          });
        }
        
        // Individual cues section
        const allCues = getAllCuesFlat();
        if (allCues.length > 0) {
          const cueHeader = document.createElement('div');
          cueHeader.className = 'countdown-dropdown-header';
          cueHeader.textContent = 'Individual Cues';
          menu.appendChild(cueHeader);
          
          allCues.forEach((cue, idx) => {
            const item = document.createElement('div');
            item.className = 'countdown-dropdown-item';
            if (currentSelectionType === 'cue' && currentSelectionIndex === idx) {
              item.classList.add('selected');
            }
            
            item.innerHTML = `
              <span class="item-name">${cue.name || 'Unnamed'}</span>
              <span class="item-meta">${formatSecondsCompact(cue.seconds || 0)}</span>
            `;
            item.addEventListener('click', (e) => {
              e.stopPropagation();
              selectDropdownItem('cue', idx);
            });
            menu.appendChild(item);
          });
        }
        
        // Empty state
        if (countdownSequences.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'countdown-dropdown-empty';
          empty.textContent = 'No sequences. Click red ring to create.';
          menu.appendChild(empty);
        }
      }
      
      function selectDropdownItem(type, index) {
        currentSelectionType = type;
        currentSelectionIndex = index;
        saveControlsSelection();
        updateSelectionLabel();
        
        // User selected something - mark as cued (different from what may be playing)
        selectionIsCued = true;
        
        // Show play button (ready to start this selection)
        const btn = document.getElementById('fullscreen-countdown-play-btn');
        if (btn) {
          btn.innerHTML = '▶';
          btn.title = 'Start';
        }
        
        // Close dropdown
        const menu = document.getElementById('fullscreen-countdown-dropdown-menu');
        if (menu) menu.classList.add('hidden');
      }
      
      function updateSelectionLabel() {
        const label = document.getElementById('fullscreen-countdown-selection');
        if (!label) return;
        
        if (currentSelectionType === 'sequence') {
          const seq = countdownSequences[currentSelectionIndex];
          label.textContent = seq ? (seq.name || '—') : '—';
        } else {
          const allCues = getAllCuesFlat();
          const cue = allCues[currentSelectionIndex];
          label.textContent = cue ? (cue.name || '—') : '—';
        }
      }
      
      function formatSecondsCompact(totalSeconds) {
        const min = Math.floor(totalSeconds / 60);
        const sec = totalSeconds % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
      }
      
      // Auto-advance when countdown completes
      function handleCountdownComplete() {
        // Clear cued flag - countdown stopped naturally
        selectionIsCued = false;
        
        const allCues = getAllCuesFlat();
        
        if (currentSelectionType === 'cue') {
          const nextIndex = currentSelectionIndex + 1;
          if (nextIndex < allCues.length) {
            currentSelectionIndex = nextIndex;
          } else {
            currentSelectionIndex = 0;
          }
        } else {
          const nextIndex = currentSelectionIndex + 1;
          if (nextIndex < countdownSequences.length) {
            currentSelectionIndex = nextIndex;
          } else {
            currentSelectionIndex = 0;
          }
        }
        
        saveControlsSelection();
        updateSelectionLabel();
      }
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        const controls = document.getElementById('fullscreen-countdown-controls');
        const menu = document.getElementById('fullscreen-countdown-dropdown-menu');
        if (controls && menu && !controls.contains(e.target)) {
          menu.classList.add('hidden');
        }
      });
      
      // Expose functions globally
      window._updateCountdownControlsPlayButton = updatePlayStopButton;
      window._handleCountdownComplete = handleCountdownComplete;
      window._refreshCountdownDropdown = () => {
        updateSelectionLabel();
        renderCountdownDropdown();
      };
      
      // Expose render function for external updates
      window._renderCountdownSequences = renderSequencesList;
      
      function cycleViewMode() {
        currentViewMode = (currentViewMode + 1) % viewModes.length;
        applyViewMode();
      }
      
      // Swap timers - swap which time shows in main vs mini
      function swapTimers() {
        showingPerformanceTime = !showingPerformanceTime;
        // Expose to global for updateCountdownDisplay to know which display is Timer
        window._showingPerformanceTime = showingPerformanceTime;
        updateLabels();
        updateTimes();
      }
      
      function applyViewMode() {
        const mode = viewModes[currentViewMode];
        const overlay = document.getElementById("stopwatch-fullscreen-overlay");
        const modeLabel = document.getElementById("fullscreen-mode-label");
        
        if (!overlay) return;
        
        // Remove all mode classes
        overlay.classList.remove("mode-solid", "mode-blur", "mode-transparent");
        overlay.classList.add(`mode-${mode}`);
        
        // Update label
        if (modeLabel) modeLabel.textContent = mode;
        
        // Handle score container blur - always remove unblur first
        mainContent.classList.remove("unblur-background");
        
        if (mode === 'blur') {
          mainContent.classList.add("blur-background-light");
          mainContent.classList.remove("blur-background");
        } else if (mode === 'solid') {
          mainContent.classList.add("blur-background");
          mainContent.classList.remove("blur-background-light");
        } else {
          // transparent - no blur
          mainContent.classList.remove("blur-background", "blur-background-light");
        }
      }
      
      function updateLabels() {
        const mainLabel = document.getElementById("fullscreen-main-label");
        const miniLabel = document.getElementById("fullscreen-mini-label");
        
        if (showingPerformanceTime) {
          if (mainLabel) mainLabel.textContent = "Performance";
          if (miniLabel) miniLabel.textContent = "Timer";
        } else {
          if (mainLabel) mainLabel.textContent = "Timer";
          if (miniLabel) miniLabel.textContent = "Performance";
        }
      }
      
      function updateTimes() {
        const mainTimeEl = document.getElementById("fullscreen-main-time");
        const miniTimeEl = document.getElementById("fullscreen-mini-time");
        const clockTimeEl = document.getElementById("fullscreen-clock-time");
        
        // Always update clock time
        if (clockTimeEl) {
          const now = new Date();
          const hours = now.getHours().toString().padStart(2, '0');
          const minutes = now.getMinutes().toString().padStart(2, '0');
          const seconds = now.getSeconds().toString().padStart(2, '0');
          clockTimeEl.textContent = `${hours}:${minutes}:${seconds}`;
        }
        
        // Compute stopwatch display fresh from raw elapsed time so it ticks
        // exactly when updateTimes() fires — not at the stopwatch's own offset
        // second boundary. This keeps it visually phase-locked with the clock.
        const swSecs = Math.floor(getStopwatchSeconds());
        const rehearsalTime = String(Math.floor(swSecs / 60)).padStart(2, '0') + ':' +
                              String(swSecs % 60).padStart(2, '0');
        const transportEl = document.getElementById("transport-time");
        const performanceTime = transportEl ? transportEl.textContent : "00:00";
        
        // Determine which element is Performance and which is Timer
        const perfTimeEl = showingPerformanceTime ? mainTimeEl : miniTimeEl;
        const timerTimeEl = showingPerformanceTime ? miniTimeEl : mainTimeEl;
        
        // Always update Performance time (it's playhead-linked)
        if (perfTimeEl) perfTimeEl.textContent = performanceTime;
        
        // Timer display — either countdown (interpolated from last server state)
        // or rehearsal stopwatch. Both rendered here so they tick with the clock.
        if (isCountdownMode && _latestCountdownState?.running) {
          const msAgo = Date.now() - _latestCountdownReceivedAt;
          const sec = Math.max(0, _latestCountdownState.remainingSec - Math.floor(msAgo / 1000));
          const m = String(Math.floor(sec / 60)).padStart(2, '0');
          const s = String(sec % 60).padStart(2, '0');
          if (timerTimeEl) timerTimeEl.textContent = `${m}:${s}`;
        } else if (!isCountdownMode) {
          if (timerTimeEl) timerTimeEl.textContent = rehearsalTime;
        }
      }
      
      function enterFullscreen() {
        
        fullscreenOverlay = createFullscreenOverlay();
        document.body.appendChild(fullscreenOverlay);
        
        // Expose state for updateCountdownDisplay
        window._showingPerformanceTime = showingPerformanceTime;
        
        // Apply initial view mode
        applyViewMode();
        
        // Start updating times — synchronised to wall-clock second boundary
        // so clock, timer, and score time all tick together visually.
        updateLabels();
        updateTimes();
        // Wait until the next whole second, then lock to 1-second intervals
        const msToNextSecond = 1000 - (Date.now() % 1000);
        clearTimeout(fullscreenStartTimer);
        fullscreenStartTimer = setTimeout(() => {
          fullscreenStartTimer = null;
          updateTimes();
          fullscreenUpdateInterval = setInterval(updateTimes, 1000);
        }, msToNextSecond);
        
        // Initialize countdown controls
        updateSelectionLabel();
        updatePlayStopButton(countdownIsRunning);
        
        window.__stopwatchFullscreen = true;
        
        // Listen for Escape key
        document.addEventListener("keydown", escapeHandler);
      }
      
      function exitFullscreen() {
        
        if (fullscreenOverlay && fullscreenOverlay.parentNode) {
          fullscreenOverlay.parentNode.removeChild(fullscreenOverlay);
          fullscreenOverlay = null;
        }
        
        // a close inside the first second: the interval is never started
        clearTimeout(fullscreenStartTimer);
        fullscreenStartTimer = null;
        if (fullscreenUpdateInterval) {
          clearInterval(fullscreenUpdateInterval);
          fullscreenUpdateInterval = null;
        }
        
        mainContent.classList.remove("blur-background", "blur-background-light");
        mainContent.classList.add("unblur-background");
        
        window.__stopwatchFullscreen = false;
        
        // Remove escape listener
        document.removeEventListener("keydown", escapeHandler);
      }
      
      function escapeHandler(e) {
        if (e.key === "Escape" && window.__stopwatchFullscreen) {
          exitFullscreen();
        }
      }
      
      // Click transport time to enter fullscreen
      transportTime.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        
        if (!window.__stopwatchFullscreen) {
          enterFullscreen();
        }
      });
    });
  }

// ============================================================================
// TRANSPORT TIME (Performance Timer)
// ============================================================================
// This timer is tied to transport playback and represents total performance
// time from first play. It keeps running during musical pauses (pause cues)
// but pauses for non-musical stops (user pressing stop).
// ============================================================================

let transportTimeStarted = false;      // Has performance started?
let transportTimeStartMs = 0;          // When current run started (Date.now())
let transportTimeAccumulated = 0;      // Accumulated time from previous runs
let transportTimeRunning = false;      // Is the timer currently running?
let transportTimeIntervalId = null;    // Interval for updating display

/**
 * Format milliseconds as MM:SS or HH:MM:SS
 */
function formatTransportTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Update the transport time display
 */
function updateTransportTimeDisplay() {
  const el = document.getElementById("transport-time");
  if (!el) return;
  
  let elapsed;
  if (transportTimeRunning) {
    elapsed = transportTimeAccumulated + (Date.now() - transportTimeStartMs);
  } else {
    elapsed = transportTimeAccumulated;
  }
  
  el.textContent = formatTransportTime(elapsed);
}

/**
 * Start the transport time (called on transport play)
 * Keeps running during musical pauses (pause cues)
 */
export function startTransportTime() {
  const el = document.getElementById("transport-time");
  
  if (!transportTimeStarted) {
    // First time starting performance - reset everything
    transportTimeStarted = true;
    transportTimeAccumulated = 0;
  }
  
  if (!transportTimeRunning) {
    transportTimeStartMs = Date.now();
    transportTimeRunning = true;
    
    // Start update interval
    if (!transportTimeIntervalId) {
      transportTimeIntervalId = setInterval(updateTransportTimeDisplay, 200);
    }
    
    if (el) {
      el.classList.add("running");
      el.classList.remove("paused");
    }
  }
}

/**
 * Pause the transport time (for non-musical stops only)
 * Musical pauses (cue pauses) should NOT call this
 */
export function pauseTransportTime() {
  // Don't pause during musical pauses
  if (transport.isMusicalPause) {
    return;
  }
  
  const el = document.getElementById("transport-time");
  
  if (transportTimeRunning) {
    transportTimeAccumulated += Date.now() - transportTimeStartMs;
    transportTimeRunning = false;
    // a paused time does not change: show it once, and stop ticking
    updateTransportTimeDisplay();
    clearInterval(transportTimeIntervalId);
    transportTimeIntervalId = null;

    if (el) {
      el.classList.remove("running");
      el.classList.add("paused");
    }
  }
}

/**
 * Reset the transport time (for new performance)
 */
export function resetTransportTime() {
  const el = document.getElementById("transport-time");
  
  transportTimeStarted = false;
  transportTimeStartMs = 0;
  transportTimeAccumulated = 0;
  transportTimeRunning = false;
  
  if (transportTimeIntervalId) {
    clearInterval(transportTimeIntervalId);
    transportTimeIntervalId = null;
  }
  
  if (el) {
    el.textContent = "00:00";
    el.classList.remove("running", "paused");
  }
}

// Expose on window for debugging and external access
window.transportTime = {
  start: startTransportTime,
  pause: pauseTransportTime,
  reset: resetTransportTime,
};

// Initialize transport time display on load
if (typeof document !== 'undefined') {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", updateTransportTimeDisplay);
  } else {
    updateTransportTimeDisplay();
  }
}
  